// Stable cache-key helpers.
//
// We prefer a content-addressed key derived from the asset's SHA-256 digest
// because it lets the same asset be reused across multiple published
// revisions without re-downloading. When a manifest predates content
// hashing, we fall back to the legacy revision-scoped key.
//
// The browser also maintains a separate response cache server-side via
// cache-control headers; this is purely the offline tier.

const CACHE_NAME = 'signage-media-v1';

export function buildAssetCacheKey(contentHash: string): string {
  return `/__signage_cache__/asset/sha256/${contentHash}`;
}

export function buildLegacyCacheKey(assetId: string, revisionId: string): string {
  return `/__signage_cache__/${revisionId}/${assetId}`;
}

/** @deprecated Use buildAssetCacheKey or buildLegacyCacheKey instead. */
export function buildCacheKey(assetId: string, revisionId: string): string {
  return buildLegacyCacheKey(assetId, revisionId);
}

export async function getCachedResponse(cacheKey: string): Promise<Response | null> {
  if (typeof caches === 'undefined') return null;
  const cache = await caches.open(CACHE_NAME);
  return (await cache.match(cacheKey)) ?? null;
}

/**
 * Minimal throttle interface accepted by `fetchAndCache`. Decoupled from the
 * concrete `BandwidthThrottle` class so this module stays free of policy
 * imports and tests can stub it cheaply.
 */
export interface FetchThrottle {
  acquire(): Promise<void>;
  release(bytes: number): void;
}

export async function fetchAndCache(
  cacheKey: string,
  remoteUrl: string,
  signal?: AbortSignal,
  throttle?: FetchThrottle | null,
): Promise<{ ok: boolean; bytes: number; error?: string }> {
  if (typeof caches === 'undefined') {
    return { ok: false, bytes: 0, error: 'cache_api_unavailable' };
  }
  if (throttle) await throttle.acquire();
  let bytes = 0;
  try {
    const res = await fetch(remoteUrl, { signal, mode: 'cors', credentials: 'omit' });
    if (!res.ok) {
      return { ok: false, bytes: 0, error: `http_${res.status}` };
    }
    const buf = await res.arrayBuffer();
    bytes = buf.byteLength;
    const stored = new Response(buf, {
      status: 200,
      headers: {
        'content-type': res.headers.get('content-type') ?? 'application/octet-stream',
        'content-length': String(buf.byteLength),
      },
    });
    const cache = await caches.open(CACHE_NAME);
    await cache.put(cacheKey, stored);
    return { ok: true, bytes: buf.byteLength };
  } catch (e: unknown) {
    return {
      ok: false,
      bytes: 0,
      error: e instanceof Error ? e.message : 'fetch_failed',
    };
  } finally {
    if (throttle) throttle.release(bytes);
  }
}

/**
 * Resolve a cache-keyed URL to a blob URL the player can hand to <img>/<video>.
 * Returns null when the asset is not cached.
 */
export async function resolveBlobUrl(cacheKey: string): Promise<string | null> {
  const res = await getCachedResponse(cacheKey);
  if (!res) return null;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * Release any blob URLs created via resolveBlobUrl. Caller is responsible for
 * tracking which URLs to revoke.
 */
export function revokeBlobUrl(url: string) {
  try {
    URL.revokeObjectURL(url);
  } catch {
    // ignore
  }
}

export async function deleteCacheKeys(cacheKeys: string[]) {
  if (typeof caches === 'undefined') return;
  const cache = await caches.open(CACHE_NAME);
  await Promise.all(cacheKeys.map((k) => cache.delete(k)));
}
